import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Skeleton,
  useTheme,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Chip,
} from "@mui/material";
import {
  KeyboardArrowUp,
  KeyboardArrowDown,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  ViewStream,
  ViewColumn,
  LiveTv,
} from "@mui/icons-material";
import apiService from "../../../../service/Api";
import { tokens } from "../../../../theme";
import {
  IStreamData,
  IThumbnailTemplate,
} from "../../../../types/thumbnail-template";
import StreamCard from "./components/StreamCardThumbnail";

// ─── Types ────────────────────────────────────────────────────────────────────

interface DeceasedPerson {
  id: number;
  first_name: string;
  second_name?: string | null;
  surname: string;
  person_image?: string | null;
}
interface ThumbnailRecord {
  id: number;
  deceased: DeceasedPerson[];
  stream: { id: number };
  thumbnail: IThumbnailTemplate | null;
}
interface StreamsApiResponse {
  results?: ApiStream[];
  next?: string | null;
}
interface ApiStream {
  id: number;
  title: string;
  date: string;
  start: string;
  end: string;
  status: string;
  youtube_stream_link?: string | null;
  website_stream_link?: string | null;
  address_one?: string | null;
  suburb?: string | null;
  city?: string | null;
  stream_thumbnail_background_image?: string | null;
  booking?: {
    deceased?: DeceasedPerson[];
    booking_type?: string;
    title?: string;
  } | null;
}
interface ThumbnailsApiResponse {
  results?: ThumbnailRecord[];
  next?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const padZ = (n: number) => String(n).padStart(2, "0");

function isUpcoming(stream: ApiStream): boolean {
  const year = parseInt(stream.date?.split("-")[0] ?? "0", 10);
  if (year < 2020) return false;

  const now = Date.now();

  const startTime = new Date(`${stream.date}T${stream.start}`).getTime();

  const endTime = stream.end
    ? new Date(`${stream.date}T${stream.end}`).getTime()
    : startTime + 2 * 60 * 60 * 1000;

  return now <= endTime;
}

function getCountdown(dateStr: string, startStr: string) {
  const diff = new Date(`${dateStr}T${startStr}`).getTime() - Date.now();
  if (diff <= 0) return { days: 0, hrs: 0, min: 0, past: true };
  return {
    days: Math.floor(diff / 86_400_000),
    hrs: Math.floor((diff % 86_400_000) / 3_600_000),
    min: Math.floor((diff % 3_600_000) / 60_000),
    past: false,
  };
}

function formatDate(dateStr?: string, startStr?: string): string {
  if (!dateStr || !startStr) return "Date/Time TBA";
  try {
    return new Date(`${dateStr}T${startStr}`).toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "Invalid Date";
  }
}

const getVenue = (s: ApiStream): string =>
  [s.address_one, s.suburb, s.city].filter(Boolean).join(", ") || "Venue Name";

// ─── CountdownTimer ───────────────────────────────────────────────────────────

function CountdownTimer({ date, start }: { date: string; start: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);
  const { days, hrs, min, past } = getCountdown(date, start);

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        justifyContent: "center",
        alignItems: "center",
        bgcolor: "background.paper",
        borderRadius: "0 0 14px 14px",
        px: 3,
        py: 1.5,
        border: "1px solid",
        borderTop: "none",
        borderColor: "divider",
        boxShadow: "0 4px 12px rgba(0,0,0,0.07)",
      }}
    >
      {past ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: 52, // same height as countdown section
            width: "100%",
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "text.secondary",
              letterSpacing: 1.5,
            }}
          >
            STREAMING NOW
          </Typography>
        </Box>
      ) : (
        [
          { val: days, label: "DAYS" },
          { val: hrs, label: "HRS" },
          { val: min, label: "MIN" },
        ].map(({ val, label }, i) => (
          <React.Fragment key={label}>
            {i > 0 && (
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 700,
                  color: "primary.main",
                  mb: 1.5,
                  px: 0.3,
                }}
              >
                :
              </Typography>
            )}
            <Box sx={{ textAlign: "center", minWidth: 40 }}>
              <Typography
                sx={{
                  fontSize: 26,
                  fontWeight: 800,
                  color: "primary.main",
                  lineHeight: 1,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {padZ(val)}
              </Typography>
              <Typography
                sx={{
                  fontSize: 8,
                  color: "text.secondary",
                  letterSpacing: 1.5,
                  mt: 0.3,
                }}
              >
                {label}
              </Typography>
            </Box>
          </React.Fragment>
        ))
      )}
    </Box>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function CardSkeleton({ width }: { width: string }) {
  return (
    <Box sx={{ flexShrink: 0, width }}>
      <Skeleton
        variant="rounded"
        height={160}
        sx={{ borderRadius: "14px 14px 0 0" }}
      />
      <Skeleton
        variant="rounded"
        height={58}
        sx={{ borderRadius: "0 0 14px 14px", mt: "1px" }}
      />
    </Box>
  );
}

// ─── Nav Arrow ────────────────────────────────────────────────────────────────

function NavArrow({
  direction,
  disabled,
  onClick,
  colors,
}: {
  direction: "up" | "down" | "left" | "right";
  disabled: boolean;
  onClick: () => void;
  colors: ReturnType<typeof tokens>;
}) {
  const icons = {
    up: <KeyboardArrowUp fontSize="small" />,
    down: <KeyboardArrowDown fontSize="small" />,
    left: <KeyboardArrowLeft fontSize="small" />,
    right: <KeyboardArrowRight fontSize="small" />,
  };
  return (
    <IconButton
      size="small"
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: 36,
        height: 36,
        bgcolor: disabled ? "action.disabledBackground" : colors.primary[400],
        color: disabled ? "action.disabled" : "#fff",
        borderRadius: "10px",
        flexShrink: 0,
        boxShadow: disabled ? "none" : "0 2px 8px rgba(0,0,0,0.18)",
        "&:hover": {
          bgcolor: disabled ? undefined : colors.primary[300],
          transform: disabled ? undefined : "scale(1.08)",
        },
        transition: "all 0.18s ease",
      }}
    >
      {icons[direction]}
    </IconButton>
  );
}

// function SectionDivider({ colors }: { colors: ReturnType<typeof tokens> }) {
//   return (
//     <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
//       <Box
//         sx={{
//           flex: 1,
//           height: "1px",
//           background: `linear-gradient(to right, transparent, ${colors.primary[400]}40)`,
//         }}
//       />
//       <Box
//         sx={{
//           width: 6,
//           height: 6,
//           borderRadius: "50%",
//           bgcolor: colors.primary[400],
//           opacity: 0.5,
//         }}
//       />
//       <Box
//         sx={{
//           flex: 1,
//           height: "1px",
//           background: `linear-gradient(to left, transparent, ${colors.primary[400]}40)`,
//         }}
//       />
//     </Box>
//   );
// }

// ─── Constants ────────────────────────────────────────────────────────────────

const VISIBLE_V = 2;
const VISIBLE_H = 3;
const GAP = 16; // px

// 3.3 means 3 full cards + ~30% of a 4th peek on the right — no dead space
const PEEK_DIVISOR = 3.3;
const CARD_WIDTH_H = `calc(${100 / PEEK_DIVISOR}% - ${(GAP * (PEEK_DIVISOR - 1)) / PEEK_DIVISOR}px)`;

// Vertical card width matches a horizontal card for visual consistency
const CARD_WIDTH_V = `calc(${100 / VISIBLE_H}% - ${(GAP * (VISIBLE_H - 1)) / VISIBLE_H}px)`;

type LayoutMode = "vertical" | "horizontal";

// ─── Main Component ───────────────────────────────────────────────────────────

interface VerticalStreamCarouselProps {
  streamsEndpoint?: string;
  thumbnailsEndpoint?: string;
}

const VerticalStreamCarousel: React.FC<VerticalStreamCarouselProps> = ({
  streamsEndpoint = "/api/streams/stream/",
  thumbnailsEndpoint = "/api/configurations/deceased-thumbnails/",
}) => {
  const [streams, setStreams] = useState<ApiStream[]>([]);
  const [thumbMap, setThumbMap] = useState<Map<number, ThumbnailRecord>>(
    new Map(),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [layout, setLayout] = useState<LayoutMode>("vertical");

  // Measured in JS after render — avoids broken nested CSS calc
  const [verticalTrackHeight, setVerticalTrackHeight] = useState<number>(600);

  const trackRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const visibleCount = layout === "vertical" ? VISIBLE_V : VISIBLE_H;

  const handleLayoutChange = (_: React.MouseEvent, val: LayoutMode | null) => {
    if (!val) return;
    setLayout(val);
    setOffset(0);
  };

  // ── Measure vertical card height after cards render ────────────────────────
  // This replaces the broken CSS nested-calc approach for TRACK_HEIGHT.
  useLayoutEffect(() => {
    if (layout !== "vertical" || !trackRef.current) return;
    const firstCard = trackRef.current.children[0] as HTMLElement | undefined;
    if (!firstCard) return;
    const cardH = firstCard.offsetHeight;
    if (cardH > 0) {
      setVerticalTrackHeight(VISIBLE_V * cardH + (VISIBLE_V - 1) * GAP);
    }
  }, [layout, streams]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        setLoading(true);

        const allStreams: ApiStream[] = [];
        let nextUrl: string | null =
          `${streamsEndpoint}?limit=100&ordering=date`;
        while (nextUrl !== null) {
          const currentUrl: string = nextUrl;
          const res: { data: StreamsApiResponse } =
            await apiService.get(currentUrl);
          if (cancelled) return;
          const page: ApiStream[] = Array.isArray(res.data)
            ? (res.data as unknown as ApiStream[])
            : (res.data?.results ?? []);
          allStreams.push(...page);
          nextUrl = res.data?.next ?? null;
        }
        if (cancelled) return;

        const allThumbs: ThumbnailRecord[] = [];
        let thumbUrl: string | null = `${thumbnailsEndpoint}?limit=100`;
        while (thumbUrl !== null) {
          const currentThumbUrl: string = thumbUrl;
          const thumbRes: { data: ThumbnailsApiResponse } =
            await apiService.get(currentThumbUrl);
          if (cancelled) return;
          const thumbPage: ThumbnailRecord[] = Array.isArray(thumbRes.data)
            ? (thumbRes.data as unknown as ThumbnailRecord[])
            : (thumbRes.data?.results ?? []);
          allThumbs.push(...thumbPage);
          thumbUrl = thumbRes.data?.next ?? null;
        }

        const map = new Map<number, ThumbnailRecord>();
        allThumbs.forEach((t) => {
          if (t.stream?.id) map.set(t.stream.id, t);
        });

        const upcoming = allStreams
          .filter(isUpcoming)
          .sort(
            (a, b) =>
              new Date(`${a.date}T${a.start}`).getTime() -
              new Date(`${b.date}T${b.start}`).getTime(),
          );

        setStreams(upcoming);
        setThumbMap(map);
      } catch {
        if (!cancelled) setError("Failed to load streams.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [streamsEndpoint, thumbnailsEndpoint]);

  // ── Scroll sync ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!trackRef.current) return;
    const el = trackRef.current;
    const target = el.children[offset] as HTMLElement | undefined;
    if (!target) return;
    if (layout === "vertical") {
      el.scrollTo({ top: target.offsetTop, behavior: "smooth" });
    } else {
      el.scrollTo({ left: target.offsetLeft, behavior: "smooth" });
    }
  }, [offset, layout]);

  // ── Nav ────────────────────────────────────────────────────────────────────
  const canPrev = offset > 0;
  const canNext = offset + visibleCount < streams.length;

  // 1 card at a time in both layouts — natural peek on the trailing edge
  const goBack = () => setOffset((o) => Math.max(0, o - 1));
  const goForward = () =>
    setOffset((o) => Math.min(streams.length - visibleCount, o + 1));

  // ── Card builder ───────────────────────────────────────────────────────────
  const buildCard = (s: ApiStream) => {
    const thumbRec = thumbMap.get(s.id);
    const cardThumbRec =
      thumbRec && thumbRec.thumbnail
        ? {
            thumbnail: thumbRec.thumbnail,
            deceased: thumbRec.deceased?.map((d) => ({
              id: d.id,
              first_name: d.first_name,
              surname: d.surname,
              second_name: d.second_name || "",
              date_of_birth: "",
              date_of_death: "",
              gender: "",
              person_image: d.person_image || "",
            })),
          }
        : undefined;

    const streamData: IStreamData = {
      id: String(s.id),
      title: s.title,
      date: s.date,
      website_stream_link: s.website_stream_link || s.youtube_stream_link || "",
      start_time: s.start,
      venue_name: getVenue(s),
      booking: s.booking
        ? {
            deceased: s.booking.deceased?.map((d) => ({
              ...d,
              second_name: d.second_name || "",
              person_image: d.person_image || "",
              date_of_birth: "",
              date_of_death: "",
              gender: "",
            })),
          }
        : undefined,
    };

    return { streamData, cardThumbRec };
  };

  const cardWrapSx = {
    flexShrink: 0,
    scrollSnapAlign: "start" as const,
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
    transition: "box-shadow 0.2s ease, transform 0.2s ease",
    "&:hover": {
      boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-2px)",
    },
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%" }}>
      {/* ══ Header ══ */}
      <Box sx={{ mb: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 4,
                height: 36,
                borderRadius: "2px",
                bgcolor: "primary.main",
                flexShrink: 0,
              }}
            />
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,

                    lineHeight: 1.1,
                    letterSpacing: "-0.3px",
                  }}
                >
                  Upcoming Streams
                </Typography>
                {!loading && streams.length > 0 && (
                  <Chip
                    icon={<LiveTv sx={{ fontSize: "14px !important" }} />}
                    label={`${streams.length} scheduled`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: 11,
                      fontWeight: 600,
                      bgcolor: `${colors.primary[400]}18`,
                      color: "primary.main",
                      border: "1px solid",
                      borderColor: `${colors.primary[400]}40`,
                      "& .MuiChip-icon": { color: "primary.main" },
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>

          <ToggleButtonGroup
            value={layout}
            exclusive
            onChange={handleLayoutChange}
            size="small"
            sx={{
              mt: 0.25,
              borderRadius: "10px",
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
              "& .MuiToggleButtonGroup-grouped": {
                border: "none",
                borderRadius: 0,
              },
              "& .MuiToggleButton-root": {
                px: 1.5,
                py: 0.75,
                color: "text.secondary",
                transition: "all 0.2s",
                "&.Mui-selected": {
                  bgcolor: colors.primary[400],
                  color: "#fff",
                  "&:hover": { bgcolor: colors.primary[300] },
                },
                "&:hover:not(.Mui-selected)": { bgcolor: "action.hover" },
              },
            }}
          >
            <Tooltip title="Stack view" placement="top">
              <ToggleButton value="vertical" aria-label="vertical layout">
                <ViewStream fontSize="small" />
              </ToggleButton>
            </Tooltip>
            <Tooltip title="Grid view" placement="top">
              <ToggleButton value="horizontal" aria-label="horizontal layout">
                <ViewColumn fontSize="small" />
              </ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {/* <SectionDivider colors={colors} /> */}

      {/* ══ Body ══ */}
      {loading ? (
        <Box sx={{ display: "flex", gap: `${GAP}px` }}>
          <CardSkeleton width={CARD_WIDTH_H} />
          <CardSkeleton width={CARD_WIDTH_H} />
          <CardSkeleton width={CARD_WIDTH_H} />
        </Box>
      ) : error ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 3,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "error.light",
          }}
        >
          <Typography color="error" fontWeight={600}>
            {error}
          </Typography>
        </Box>
      ) : streams.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 7,
            px: 3,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "divider",
          }}
        >
          <LiveTv sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
          <Typography
            sx={{ color: "text.secondary", fontSize: 14, fontWeight: 500 }}
          >
            No upcoming streams at the moment.
          </Typography>
          <Typography sx={{ color: "text.disabled", fontSize: 12, mt: 0.5 }}>
            Check back soon.
          </Typography>
        </Box>
      ) : layout === "vertical" ? (
        // ════════ VERTICAL — fixed-height window, cards slide inside it ════════
        // Width matches a single horizontal card. Height is measured in JS from
        // the first rendered card so it shows exactly VISIBLE_V cards at a time.
        <Box sx={{ position: "relative", width: CARD_WIDTH_V }}>
          {/* Top gradient + up arrow */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              display: "flex",
              justifyContent: "center",
              background:
                "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, transparent 100%)",
              pt: 0.5,
              pb: 5,
              borderRadius: "14px 14px 0 0",
              opacity: canPrev ? 1 : 0,
              pointerEvents: canPrev ? "auto" : "none",
              transition: "opacity 0.25s ease",
            }}
          >
            <NavArrow
              direction="up"
              disabled={!canPrev}
              onClick={goBack}
              colors={colors}
            />
          </Box>

          {/* Fixed-height clipping window — height set from JS measurement */}
          <Box
            sx={{
              overflow: "hidden",
              borderRadius: "14px",
              height: verticalTrackHeight,
            }}
          >
            <Box
              ref={trackRef}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: `${GAP}px`,
                overflowY: "scroll",
                scrollBehavior: "smooth",
                scrollSnapType: "y mandatory",
                height: "100%",
                "&::-webkit-scrollbar": { display: "none" },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {streams.map((s) => {
                const { streamData, cardThumbRec } = buildCard(s);

                const now = Date.now();

                const startTime = new Date(`${s.date}T${s.start}`).getTime();

                const endTime = new Date(`${s.date}T${s.end}`).getTime();

                const isLive = now >= startTime && now <= endTime;

                return (
                  <Box
                    key={s.id}
                    sx={{
                      ...cardWrapSx,
                      width: "100%",
                      flexShrink: 0,
                      position: "relative",
                    }}
                  >
                    {getCountdown(s.date, s.start).past && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 12,
                          left: 12,
                          zIndex: 5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.7,
                          px: 1.2,
                          py: 0.5,
                          borderRadius: "999px",
                          bgcolor: "#d32f2f",
                          color: "#fff",
                          fontSize: 11,
                          fontWeight: 800,
                          letterSpacing: 0.8,
                          boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
                          animation: "pulse 1.5s infinite",
                        }}
                      >
                        {isLive && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 12,
                              left: 12,
                              zIndex: 5,
                              bgcolor: "error.main",
                              color: "#fff",
                              px: 1.2,
                              py: 0.5,
                              borderRadius: "999px",
                              fontSize: 11,
                              fontWeight: 700,
                            }}
                          >
                            LIVE NOW
                          </Box>
                        )}
                      </Box>
                    )}

                    <StreamCard
                      stream={streamData}
                      thumbRec={cardThumbRec}
                      formatDate={formatDate}
                    />

                    <CountdownTimer date={s.date} start={s.start} />
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Bottom gradient + down arrow — sibling of the clip window, NOT inside it */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              display: "flex",
              justifyContent: "center",
              background:
                "linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)",
              pb: 0.5,
              pt: 5,
              borderRadius: "0 0 14px 14px",
              opacity: canNext ? 1 : 0,
              pointerEvents: canNext ? "auto" : "none",
              transition: "opacity 0.25s ease",
            }}
          >
            <NavArrow
              direction="down"
              disabled={!canNext}
              onClick={goForward}
              colors={colors}
            />
          </Box>
        </Box>
      ) : (
        // ════════ HORIZONTAL — 3 full cards + right-edge peek, no dead space ════════
        // NO overflow:hidden wrapper — the track's own CSS hides the scrollbar,
        // and removing the wrapper lets the peeking 4th card show on the right.
        <Box sx={{ position: "relative" }}>
          {/* Left gradient + arrow */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              background:
                "linear-gradient(to right, rgba(0,0,0,0.45) 0%, transparent 100%)",
              pl: 0.5,
              pr: 4,
              borderRadius: "14px 0 0 14px",
              opacity: canPrev ? 1 : 0,
              pointerEvents: canPrev ? "auto" : "none",
              transition: "opacity 0.25s ease",
            }}
          >
            <NavArrow
              direction="left"
              disabled={!canPrev}
              onClick={goBack}
              colors={colors}
            />
          </Box>

          {/* Track — NO overflow:hidden wrapper around this */}
          <Box
            ref={trackRef}
            sx={{
              display: "flex",
              gap: `${GAP}px`,
              overflowX: "scroll",
              scrollBehavior: "smooth",
              scrollSnapType: "x mandatory",
              "&::-webkit-scrollbar": { display: "none" },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
            }}
          >
            {streams.map((s) => {
              const { streamData, cardThumbRec } = buildCard(s);
              const now = Date.now();

              const startTime = new Date(`${s.date}T${s.start}`).getTime();

              const endTime = new Date(`${s.date}T${s.end}`).getTime();

              const isLive = now >= startTime && now <= endTime;
              return (
                <Box
                  key={s.id}
                  sx={{
                    ...cardWrapSx,
                    width: CARD_WIDTH_H,
                    position: "relative",
                  }}
                >
                  {isLive && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 12,
                        left: 12,
                        zIndex: 5,
                        display: "flex",
                        alignItems: "center",
                        gap: 0.7,
                        px: 1.2,
                        py: 0.5,
                        borderRadius: "999px",
                        bgcolor: "#d32f2f",
                        color: "#fff",
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: 0.8,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
                        animation: "pulse 1.5s infinite",
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#fff",
                        }}
                      />
                      LIVE NOW
                    </Box>
                  )}

                  <StreamCard
                    stream={streamData}
                    thumbRec={cardThumbRec}
                    formatDate={formatDate}
                  />

                  <CountdownTimer date={s.date} start={s.start} />
                </Box>
              );
            })}
          </Box>

          {/* Right gradient + arrow */}
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              background:
                "linear-gradient(to left, rgba(0,0,0,0.45) 0%, transparent 100%)",
              pr: 0.5,
              pl: 4,
              borderRadius: "0 14px 14px 0",
              opacity: canNext ? 1 : 0,
              pointerEvents: canNext ? "auto" : "none",
              transition: "opacity 0.25s ease",
            }}
          >
            <NavArrow
              direction="right"
              disabled={!canNext}
              onClick={goForward}
              colors={colors}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default VerticalStreamCarousel;
